/** ກວດ PWA standalone (iOS / Android / desktop installed) */
export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator &&
      (navigator as Navigator & { standalone?: boolean }).standalone === true)
  );
}

/**
 * Chrome --kiosk-printing: print() silent, afterprint ມັກບໍ່ fire
 * localStorage pos:kiosk-printing = "1" force, "0" ປິດ auto-detect
 */
export function isKioskPrintingMode(): boolean {
  if (typeof window === "undefined") return false;

  const override = localStorage.getItem("pos:kiosk-printing");
  if (override === "1") return true;
  if (override === "0") return false;

  const isChrome =
    /Chrome/i.test(navigator.userAgent) && !/Edg/i.test(navigator.userAgent);
  const isFullscreen =
    window.matchMedia("(display-mode: fullscreen)").matches ||
    Math.abs(window.innerHeight - screen.height) < 80;

  return isChrome && isFullscreen;
}

function extractPrintParts(combinedHtml: string): {
  styles: string;
  bodyHtml: string;
} {
  const styleMatch = combinedHtml.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
  const bodyMatch = combinedHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const bodyHtml = (bodyMatch?.[1] ?? combinedHtml).replace(
    /<script\b[^>]*>[\s\S]*?<\/script>/gi,
    "",
  );
  return {
    styles: styleMatch?.[1] ?? "",
    bodyHtml,
  };
}

function waitForImages(root: HTMLElement, timeoutMs: number): Promise<void> {
  const imgs = Array.from(root.querySelectorAll("img"));
  const pending = imgs.filter((img) => !img.complete);
  if (pending.length === 0) return Promise.resolve();

  return new Promise((resolve) => {
    let left = pending.length;
    const done = () => {
      left -= 1;
      if (left <= 0) resolve();
    };
    for (const img of pending) {
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    }
    window.setTimeout(resolve, timeoutMs);
  });
}

/** ລ້າງ scroll-lock / overlay ຄ້າຫຼັງ window.print() ໃນ PWA / kiosk */
export function releaseStaleModalLocks(): void {
  if (typeof document === "undefined") return;
  document.body.style.removeProperty("pointer-events");
  document.body.style.removeProperty("overflow");
  document.documentElement.style.removeProperty("overflow");
  document.body.removeAttribute("data-scroll-locked");
  Array.from(document.querySelectorAll("[data-radix-focus-guard]")).forEach(
    (el) => {
      el.remove();
    },
  );
  Array.from(document.querySelectorAll("[data-radix-dialog-overlay]")).forEach(
    (el) => {
      el.remove();
    },
  );
}

function refocusAppWindow(): void {
  window.focus();
  window.dispatchEvent(new Event("focus"));
}

/**
 * ຟັງວ່າ print dialog / preview ປິດແລ້ວ
 * ໃຊ້ wasPrinting ເພື່ອບໍ່ cleanup ກ່ອນ print() ເລີ່ມ
 */
function listenForPrintDialogClose(
  win: Window,
  onClose: () => void,
): () => void {
  let done = false;
  let wasPrinting = false;

  const finish = () => {
    if (done) return;
    done = true;
    onClose();
  };

  win.addEventListener("afterprint", finish, { once: true });

  let mql: MediaQueryList | null = null;
  let onMqlChange: ((event: MediaQueryListEvent) => void) | null = null;

  try {
    mql = win.matchMedia("print");
    wasPrinting = mql.matches;
    onMqlChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        wasPrinting = true;
        return;
      }
      if (wasPrinting) {
        finish();
      }
    };
    mql.addEventListener("change", onMqlChange);
  } catch {
    // ignore
  }

  return () => {
    if (mql && onMqlChange) {
      mql.removeEventListener("change", onMqlChange);
    }
  };
}

function finishPrintSession(cleanup: () => void): void {
  refocusAppWindow();
  releaseStaleModalLocks();
  cleanup();
}

/**
 * ພິມຈາກ main window — --kiosk-printing ເຮັດວຽກຖືກກັບ path ນີ້
 * (popup/iframe ມັກເປີດ print preview ແລ້ວບໍ່ປິດເອງ)
 */
export function printHtmlInMainDocument(combinedHtml: string): void {
  const { styles, bodyHtml } = extractPrintParts(combinedHtml);
  const rootId = `pos-print-root-${Date.now()}`;
  const styleId = `pos-print-style-${Date.now()}`;

  const root = document.createElement("div");
  root.id = rootId;
  root.setAttribute("aria-hidden", "true");
  root.innerHTML = bodyHtml;

  const styleEl = document.createElement("style");
  styleEl.id = styleId;
  styleEl.textContent = `
    @media screen {
      #${rootId} {
        position: fixed;
        left: -9999px;
        top: 0;
        width: 1px;
        height: 1px;
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
      }
    }
    @media print {
      body > *:not(#${rootId}) {
        display: none !important;
      }
      #${rootId} {
        display: block !important;
        position: static !important;
        left: auto !important;
        width: auto !important;
        height: auto !important;
        opacity: 1 !important;
        overflow: visible !important;
        pointer-events: auto !important;
      }
    }
    ${styles}
  `;

  document.head.appendChild(styleEl);
  document.body.appendChild(root);

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    root.remove();
    styleEl.remove();
    refocusAppWindow();
    releaseStaleModalLocks();
  };

  const delayMs = isStandalonePwa() || isKioskPrintingMode() ? 600 : 200;

  window.setTimeout(() => {
    void waitForImages(root, 3_000).then(() => {
      let detachListeners = () => {};

      try {
        detachListeners = listenForPrintDialogClose(window, () => {
          finishPrintSession(cleanup);
        });

        window.focus();
        window.print();

        if (isKioskPrintingMode()) {
          window.setTimeout(() => {
            if (!cleaned) {
              finishPrintSession(cleanup);
            }
          }, 800);
        }
      } catch {
        detachListeners();
        cleanup();
        throw new Error("Cannot open print dialog");
      }

      window.setTimeout(() => {
        detachListeners();
        if (!cleaned) {
          finishPrintSession(cleanup);
        }
      }, 120_000);
    });
  }, delayMs);
}

/**
 * ພິມຜ່ານ hidden iframe — fallback ສຸດທ້າຍ
 */
export function printHtmlInHiddenFrame(combinedHtml: string): void {
  const { styles, bodyHtml } = extractPrintParts(combinedHtml);

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.title = "print";
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none";

  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!win || !doc) {
    iframe.remove();
    throw new Error("Cannot create print frame");
  }

  doc.open();
  doc.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${styles}</style></head><body>${bodyHtml}</body></html>`,
  );
  doc.close();

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    window.setTimeout(() => {
      iframe.remove();
      refocusAppWindow();
      releaseStaleModalLocks();
    }, 100);
  };

  const delayMs = isStandalonePwa() ? 600 : 200;

  window.setTimeout(() => {
    let detachListeners = () => {};

    try {
      detachListeners = listenForPrintDialogClose(win, () => {
        finishPrintSession(cleanup);
      });

      win.focus();
      win.print();

      if (isKioskPrintingMode()) {
        window.setTimeout(() => {
          if (!cleaned) {
            finishPrintSession(cleanup);
          }
        }, 800);
      }
    } catch {
      detachListeners();
      cleanup();
      throw new Error("Cannot open print dialog");
    }

    window.setTimeout(() => {
      detachListeners();
      if (!cleaned) {
        finishPrintSession(cleanup);
      }
    }, 120_000);
  }, delayMs);
}

/** popup ນອກຈໍ — fallback ເມື່ອ main document ບໍ່ເໝາະ */
export function printHtmlInOffscreenPopup(combinedHtml: string): boolean {
  if (typeof window.open !== "function") return false;

  const printWindow = window.open(
    "",
    "_blank",
    "width=800,height=600,left=-10000,top=-10000",
  );
  if (!printWindow) return false;

  const { styles, bodyHtml } = extractPrintParts(combinedHtml);
  printWindow.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${styles}</style></head><body>${bodyHtml}</body></html>`,
  );
  printWindow.document.close();

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    window.setTimeout(() => {
      try {
        printWindow.close();
      } catch {
        // ignore
      }
      refocusAppWindow();
      releaseStaleModalLocks();
    }, 100);
  };

  printWindow.focus();

  window.setTimeout(() => {
    let detachListeners = () => {};

    try {
      detachListeners = listenForPrintDialogClose(printWindow, () => {
        finishPrintSession(cleanup);
      });

      printWindow.focus();
      printWindow.print();
    } catch {
      detachListeners();
      cleanup();
    }

    window.setTimeout(() => {
      detachListeners();
      if (!cleaned) {
        finishPrintSession(cleanup);
      }
    }, 120_000);
  }, 300);

  return true;
}

/** main window ກ່ອນ (kiosk-printing), fallback popup → iframe */
export function printHtmlDocument(combinedHtml: string): void {
  printHtmlInMainDocument(combinedHtml);
}

/** ລໍຖ້າ modal ປິດ ແລ້ວຄ່ອຍພິມ */
export function scheduleDeferredPrint(
  printFn: () => void | Promise<void>,
  delayMs = 800,
): void {
  window.setTimeout(() => {
    void Promise.resolve()
      .then(printFn)
      .catch((error) => {
        console.error("[pos-print] deferred print failed", error);
      });
  }, delayMs);
}

export function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });
}
